import bookingService from '@/services/bookingService';
import spaceBookingService from '@/services/spaceBookingService';
import courseReservationService from '@/services/courseReservationService';

function ymd(val) {
  if (val == null || val === '') return '';
  return String(val).split('T')[0].substring(0, 10);
}

function isCancelled(b) {
  return String(b.status || '').toLowerCase() === 'cancelled';
}

/**
 * فترة العهدة [fFrom, fTo]: تُحسب الحجوزات التي date_from >= fFrom و date_from <= fTo فقط.
 * لا يُستخدم date_to للحجز في شرط التضمين.
 */
function reservationStartInPeriod(startYmd, fFrom, fTo) {
  if (!startYmd) return false;
  return startYmd >= fFrom && startYmd <= fTo;
}

/** قاعات: date_from للحجز، أو أي detail.date ضمن الفترة */
function roomBookingInPeriod(b, fFrom, fTo) {
  if (isCancelled(b)) return false;

  if (Array.isArray(b.details) && b.details.length) {
    return b.details.some(d => reservationStartInPeriod(ymd(d.date), fFrom, fTo));
  }

  return reservationStartInPeriod(ymd(b.date_from), fFrom, fTo);
}

function netRoom(b) {
  const n =
    Number(b.net_price ?? (Number(b.total_price ?? 0) - Number(b.discount ?? 0))) || 0;
  return n >= 0 ? n : 0;
}

/** صافي = total − discount؛ المدفوع = الصافي − المتبقي (كإيصال الحجز) */
export function summarizeRoomBookings(bookings, dateFrom, dateTo) {
  if (!Array.isArray(bookings))
    return { total_bookings: 0, total_amount: 0, total_paid: 0, total_remaining: 0 };

  let count = 0;
  let totalAmount = 0;
  let totalPaid = 0;
  let totalRemaining = 0;

  for (const b of bookings) {
    if (!roomBookingInPeriod(b, dateFrom, dateTo)) continue;
    count += 1;
    const net = netRoom(b);
    const remaining = Number(b.remaining ?? 0) || 0;
    const paid = Math.max(net - remaining, 0);
    totalAmount += net;
    totalPaid += paid;
    totalRemaining += remaining;
  }

  return {
    total_bookings: count,
    total_amount: totalAmount,
    total_paid: totalPaid,
    total_remaining: totalRemaining
  };
}

function spaceInPeriod(b, fFrom, fTo) {
  if (isCancelled(b)) return false;
  return reservationStartInPeriod(ymd(b.date_from), fFrom, fTo);
}

function netSpace(b) {
  return Number(Number(b.total_price ?? 0) - Number(b.discount ?? 0)) || 0;
}

export function summarizeSpaceBookings(bookings, dateFrom, dateTo) {
  if (!Array.isArray(bookings))
    return { total_bookings: 0, total_amount: 0, total_paid: 0, total_remaining: 0 };

  let count = 0;
  let totalAmount = 0;
  let totalPaid = 0;
  let totalRemaining = 0;

  for (const b of bookings) {
    if (!spaceInPeriod(b, dateFrom, dateTo)) continue;
    count += 1;
    const net = netSpace(b);
    const remaining = Number(b.remaining ?? 0) || 0;
    const paid = Math.max(net - remaining, 0);
    totalAmount += net;
    totalPaid += paid;
    totalRemaining += remaining;
  }

  return {
    total_bookings: count,
    total_amount: totalAmount,
    total_paid: totalPaid,
    total_remaining: totalRemaining
  };
}

function courseInPeriod(b, fFrom, fTo) {
  if (isCancelled(b)) return false;
  const start = ymd(b.date_from || b.course?.date_from || b.booking_date);
  return reservationStartInPeriod(start, fFrom, fTo);
}

function netCourse(b) {
  if (b.net_price != null && Number(b.net_price)) return Number(b.net_price) || 0;
  return Number(Number(b.total_price ?? 0) - Number(b.discount ?? 0)) || 0;
}

export function summarizeCourseReservations(bookings, dateFrom, dateTo) {
  if (!Array.isArray(bookings))
    return { total_bookings: 0, total_amount: 0, total_paid: 0, total_remaining: 0 };

  let count = 0;
  let totalAmount = 0;
  let totalPaid = 0;
  let totalRemaining = 0;

  for (const b of bookings) {
    if (!courseInPeriod(b, dateFrom, dateTo)) continue;
    count += 1;
    const net = netCourse(b);
    const remaining = Number(b.remaining ?? 0) || 0;
    const paid = Math.max(net - remaining, 0);
    totalAmount += net;
    totalPaid += paid;
    totalRemaining += remaining;
  }

  return {
    total_bookings: count,
    total_amount: totalAmount,
    total_paid: totalPaid,
    total_remaining: totalRemaining
  };
}

/** يستخرج المصفوفة من أغلفة الاستجابة الشائعة (room / space / course) */
export function extractListPayload(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  let d = body.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d?.data?.data && Array.isArray(d.data.data)) return d.data.data;
  return [];
}

async function loadRoomBookingRows() {
  const res = await bookingService.fetchRoomBookingsWithDetails();
  return extractListPayload(res?.data);
}

async function loadSpaceBookingRows() {
  const body = await spaceBookingService.getSpaceBookings({});
  return extractListPayload(body);
}

async function loadCourseReservationRows() {
  const body = await courseReservationService.getReservations({ _no_pagination_: '1' });
  return extractListPayload(body);
}

/** uiType: 'room' | 'space' | 'course' كما في واجهة العهدة */
export async function fetchAndSummarizeCustodyPeriod(uiType, dateFrom, dateTo) {
  const from = String(dateFrom).substring(0, 10);
  const to = String(dateTo).substring(0, 10);

  if (uiType === 'space') {
    const rows = await loadSpaceBookingRows();
    return summarizeSpaceBookings(rows, from, to);
  }
  if (uiType === 'course') {
    const rows = await loadCourseReservationRows();
    return summarizeCourseReservations(rows, from, to);
  }
  const rows = await loadRoomBookingRows();
  return summarizeRoomBookings(rows, from, to);
}
